
import React from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Brain, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Target,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

interface JobExecutiveSummaryCardProps {
  job: Job;
}

export const JobExecutiveSummaryCard: React.FC<JobExecutiveSummaryCardProps> = ({ job }) => {
  // Mock AI-generated insights (in real app, this would come from analytics)
  const insights = {
    summary: "Job is performing well with above-average application quality. Consider expanding to LinkedIn for premium candidates.",
    keyMetrics: {
      daysOpen: Math.floor((new Date().getTime() - new Date(job.created_at || '').getTime()) / (1000 * 60 * 60 * 24)),
      applicationsReceived: 24,
      conversionRate: 12.5,
      averageTimeToHire: 21
    },
    aiInsights: [
      { type: 'positive', text: 'Above average candidate quality', icon: CheckCircle },
      { type: 'negative', text: 'Application volume below target', icon: AlertCircle },
      { type: 'neutral', text: 'Interview conversion rate on track', icon: Target }
    ],
    nextActions: [
      'Post to LinkedIn Jobs to reach senior candidates',
      'Review and optimize job description keywords',
      'Schedule calls with top 3 screening candidates'
    ]
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'positive': return 'text-green-600 dark:text-green-400';
      case 'negative': return 'text-red-600 dark:text-red-400';
      default: return 'text-blue-600 dark:text-blue-400';
    }
  };

  const getProgressColor = (value: number, benchmark: number) => {
    if (value >= benchmark) return 'bg-green-500';
    if (value >= benchmark * 0.8) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          AI Job Performance Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {insights.summary}
        </p>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-muted/20 rounded-lg">
            <div className="text-2xl font-bold text-primary">{insights.keyMetrics.daysOpen}</div>
            <div className="text-xs text-muted-foreground">Days Open</div>
          </div>
          <div className="text-center p-3 bg-muted/20 rounded-lg">
            <div className="text-2xl font-bold text-primary">{insights.keyMetrics.applicationsReceived}</div>
            <div className="text-xs text-muted-foreground">Applications</div>
          </div>
          <div className="text-center p-3 bg-muted/20 rounded-lg">
            <div className="text-2xl font-bold text-primary">{insights.keyMetrics.conversionRate}%</div>
            <div className="text-xs text-muted-foreground">Conversion Rate</div>
          </div>
          <div className="text-center p-3 bg-muted/20 rounded-lg">
            <div className="text-2xl font-bold text-primary">{insights.keyMetrics.averageTimeToHire}</div>
            <div className="text-xs text-muted-foreground">Avg. Time to Hire</div>
          </div>
        </div>

        {/* AI Insights */}
        <div>
          <h4 className="font-medium mb-3">AI Performance Insights</h4>
          <div className="space-y-2">
            {insights.aiInsights.map((insight, index) => {
              const Icon = insight.icon;
              return (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <Icon className={`h-4 w-4 ${getInsightColor(insight.type)}`} />
                  <span className="text-muted-foreground">{insight.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Performance Benchmarks */}
        <div>
          <h4 className="font-medium mb-3">Performance vs. Benchmarks</h4>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Application Volume</span>
                <span>24 / 30 target</span>
              </div>
              <Progress value={80} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Quality Score</span>
                <span>85 / 75 benchmark</span>
              </div>
              <Progress value={113} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Time to Fill</span>
                <span>On track (21 days avg.)</span>
              </div>
              <Progress value={95} className="h-2" />
            </div>
          </div>
        </div>

        {/* Recommended Next Actions */}
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-1">
            <Target className="h-4 w-4" />
            Recommended Next Actions
          </h4>
          <div className="space-y-2">
            {insights.nextActions.map((action, index) => (
              <div key={index} className="flex items-start gap-2 text-sm">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                <span className="text-muted-foreground">{action}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
